import React, { useMemo } from 'react';
import { FormSelect } from '@kineticdata/react';
import {
  Status,
  SuggestionsContainer,
  generateInput,
  generateMultipleSelectionsContainer,
  generateSingleSelectionsContainer,
  generateMultipleSelection,
  generateSingleSelection,
} from './TypeaheadDefaults';
import { FieldWrapper } from './FieldWrapper';
import classNames from 'classnames';
import { parseRenderOptions } from './utils';

const selectionToValue = selection =>
  `${selection.get('name')} <${selection.get('slug')}>`;

const Suggestion = ({ suggestion, active, custom }) => (
  <div className={classNames('suggestion', { active })}>
    <div className="large">{suggestion.get('name')}</div>
    <div className="small">{suggestion.get('slug')}</div>
  </div>
);

export const FormComponent = ({
  multiple,
  renderOptions,
  components,
  ...props
}) => (
  <FieldWrapper {...props} renderOptions={renderOptions} fieldset={multiple}>
    <FormSelect
      components={{ Status, SuggestionsContainer, Suggestion, ...components }}
      disabled={!props.enabled}
      minSearchLength={1}
      multiple={multiple}
      id={props.id}
      value={props.value}
      onChange={props.onChange}
      onBlur={props.onBlur}
      onFocus={props.onFocus}
      placeholder={renderOptions.placeholder}
      search={props.search}
      form={props.form}
      invalid={renderOptions.hasErrors}
    />
  </FieldWrapper>
);

/******************************************************************************
 * Single Field
 ******************************************************************************/

export const FormField = props => {
  const renderOptions = parseRenderOptions(props);
  const components = useMemo(
    () => ({
      Input: generateInput({
        size: renderOptions.size,
        className: renderOptions.fieldClassName,
      }),
      Selection: generateSingleSelection(selectionToValue, {
        size: renderOptions.size,
      }),
      SelectionsContainer: generateSingleSelectionsContainer({
        size: renderOptions.size,
      }),
    }),
    [renderOptions.size, renderOptions.fieldClassName],
  );

  return (
    <FormComponent
      renderOptions={renderOptions}
      components={components}
      {...props}
    />
  );
};

/******************************************************************************
 * Multi Field
 ******************************************************************************/

export const FormMultiField = props => {
  const renderOptions = parseRenderOptions(props, { isFloating: false });
  const components = useMemo(
    () => ({
      Input: generateInput({
        size: renderOptions.size,
        className: renderOptions.fieldClassName,
      }),
      Selection: generateMultipleSelection(selectionToValue, {
        size: renderOptions.size,
      }),
      SelectionsContainer: generateMultipleSelectionsContainer({
        size: renderOptions.size,
      }),
    }),
    [renderOptions.size, renderOptions.fieldClassName],
  );
  return (
    <FormComponent
      multiple={true}
      renderOptions={renderOptions}
      components={components}
      {...props}
    />
  );
};
